/*=========================================================================================
File Name: router.js
Description: Routes for vue-router. Lazy loading is enabled.
----------------------------------------------------------------------------------------
Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
Author: Pixinvent
Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/
import Vue from 'vue';
import Router from 'vue-router';
import store from './store/store.js';

Vue.use(Router)

// =============================================================================
// AUTH CHECK
// =============================================================================
function requireAuth(to, from, next) {
    localStorage.setItem('isPartnerView', false);
    localStorage.removeItem('isPartnerLoggedIn');
    localStorage.removeItem('clientRebateStatus');
    localStorage.removeItem('loggedInPartnerId');
    localStorage.removeItem('loggedInPartnerName');
    localStorage.removeItem('clientToken');

    store.dispatch('setClientToken', null);
    store.dispatch('setClientRebateStatus', null);
    /*
    	Determines where we should send the user.
    */
    function proceed() {
        /*
        	If the user is not empty, that means there's a user
        	authenticated we allow them to continue. Otherwise, we
        	send the user back to the home page.
        */
        if (JSON.parse(localStorage.getItem('userDetails'))) {
            router.app.$acl.change(JSON.parse(localStorage.getItem('userDetails')).role);
            next();
        } else {
            // Clear user details
            store.dispatch('auth/clearAuthenticatedUser');
            // Redirect to login
            next({
                path: '/login',
                query: { redirect: to.fullPath }
            });
        }
    }

    /*
    	Auth Check AXIOS call
    */
    router.app.$axiosSecure.get(`/auth-check`)
    .then((response) => {
        if (response) {
            // Proceed to route
            proceed();
        } else {
            // Clear user details
            store.dispatch('auth/clearAuthenticatedUser');
            // Redirect to login
            next({
                path: '/login',
                query: { redirect: to.fullPath }
            });
        }
    })
    .catch(error => {
        // If user is not logged in
        if (error.response && error.response.status == 401) {
            // Clear user details
            store.dispatch('auth/clearAuthenticatedUser');
            // Redirect to login
            next({
                path: '/login',
                query: { redirect: to.fullPath }
            });
        }
    })
}

// For Client View Routes
function requireClientAuth(to, from, next) {
    localStorage.setItem('isPartnerView', true);
    localStorage.setItem('loggedInPartnerName', to.params.partnerName);

    /*
    	Determines where we should send the user.
    */
    function proceed() {
        next();
    }

    /*
    	Client Auth Check
    */
   if(localStorage.getItem('isPartnerLoggedIn')) {
        proceed();
   } else {
        next({
            path: '/partner/sign-on',
            query: { redirect: to.fullPath, partnerName: to.params.partnerName }
        });
   }
}

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior() {
        return { x: 0, y: 0 }
    },
    routes: [{
        // =============================================================================
        // MAIN LAYOUT ROUTES
        // =============================================================================
            path: '',
            component: () =>
                import ('./layouts/main/Main.vue'),
            children: [
                // =============================================================================
                // Theme Routes
                // =============================================================================
                {
                    path: '/',
                    name: 'Dashboard',
                    component: () =>
                        import ('./views/Dashboard.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        pageTitle: 'Dashboard',
                        rule: 'isGuest'
                    },
                },

                // NOTE: Promo Routes
                {
                    path: '/promos/add/cnic',
                    name: 'Add Promo - CNIC',
                    component: () =>
                        import ('./views/promos/AddCNIC.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'Add Promo - CNIC',
                        // permission: 'user'
                        rule: 'isAllowedToAddPromo'
                    }
                },
                {
                    path: '/promos/add/tucows',
                    name: 'Add Promo - Tucows',
                    component: () =>
                        import ('./views/promos/AddTucows.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'Add Promo - Tucows',
                        // permission: 'user'
                        rule: 'isAllowedToAddPromo'
                    }
                },
                {
                    path: '/promos/requests',
                    name: 'Promo Requests',
                    component: () =>
                        import ('./views/promos/Requests.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'Requests',
                        rule: 'isChannelUser'
                    }
                },
                {
                    path: '/promos/lists',
                    name: 'Promo Lists',
                    component: () =>
                        import ('./views/promos/Lists.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'Lists',
                        rule: 'isGuest'
                    }
                },
                {
                    path: '/promos/expiring',
                    name: 'Promo Updates',
                    component: () =>
                        import ('./views/promos/Updates.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'Expiring Promos',
                        rule: 'isChannelUser'
                    }
                },

                // NOTE: Agreement Routes
                {
                    path: '/agreements/lists',
                    name: 'Agreements',
                    component: () =>
                        import ('./views/agreements/List.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'View Agreements',
                        rule: 'isGuest'
                    }
                },
                {
                    path: '/agreements/requests',
                    name: 'Agreements Requests',
                    component: () =>
                        import ('./views/agreements/Requests.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'Requests',
                        rule: 'isChannelUser'
                    }
                },

                // NOTE: User Management Routes
                {
                    path: '/user-management',
                    name: 'User Management',
                    component: () =>
                        import ('./views/user-management/List.vue'),
                        beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'User Management',
                        rule: 'isChannelAdmin'
                    }
                },

                // NOTE: Partner Routes
                {
                    path: '/partners/add-registrar/cnic',
                    name: 'Add Registrar - CNIC',
                    component: () => 
                        import ('./views/partners/AddCNICRegistrar.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: '',
                        rule: 'isAllowedToAddAndEditPartners'
                    }
                },
                {
                    path: '/partners/add-registrar/tucows',
                    name: 'Add Registrar - Tucows',
                    component: () => 
                        import ('./views/partners/AddTucowsRegistrar.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: '',
                        rule: 'isAllowedToAddAndEditPartners'
                    }
                },
                {
                    path: '/partners/add-reseller',
                    name: 'Add Reseller',
                    component: () => 
                        import ('./views/partners/AddReseller.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: '',
                        rule: 'isAllowedToAddAndEditPartners'
                    }
                },
                {
                    path: '/partners/lists',
                    name: 'View Partners',
                    component: () =>
                        import ('./views/partners/List.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'View Partners',
                        rule: 'isAllowedToViewResellers'
                    }
                },
                {
                    path: '/partners/requests',
                    name: 'View Reseller Requests',
                    component: () =>
                        import ('./views/partners/ViewResellerRequests.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'View Reseller Approval Requests',
                        rule: 'isAllowedToViewResellers'
                    }
                },
                {
                    path: '/partners/overview',
                    name: 'Partner Overview',
                    component: () =>
                        import ('./views/partners/Clients.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'Partner Overview',
                        rule: 'isAllowedToAddAndEditPartners'
                    }
                },

                // NOTE: Rebates Routes
                {
                    path: '/rebates/scheduler',
                    name: 'Rebates Scheduler',
                    component: () =>
                        import ('./views/rebates/RebatesScheduler.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'Rebates Scheduler',
                        rule: 'isAllowedToViewRebates'
                    }
                },
                {
                    path: '/rebates/calculator',
                    name: 'Rebates Calculator',
                    component: () =>
                        import ('./views/rebates/RebatesCalculator.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'Rebates Calculator',
                        rule: 'isAllowedToViewRebates'
                    }
                },
                // TODO: Uncomment when UI for payments is ready
                // {
                //     path: '/rebates/payments',
                //     name: 'Payments',
                //     component: () =>
                //         import ('./views/rebates/Payments.vue'),
                //     beforeEnter: requireAuth,
                //     meta: {
                //         buttons: {
                //             refresh: true
                //         },
                //         pageTitle: 'Payments',
                        // rule: 'isAllowedToViewRebates'
                //     }
                // },
                {
                    path: '/rebates/cnic-compiler',
                    name: 'CNIC Compiler',
                    component: () =>
                        import ('./views/rebates/CNICCompiler.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'CNIC Compiler',
                        rule: 'isAllowedToViewRebates'
                    }
                },
                // NOTE: Manual Rebates Routes
                {
                    path: '/manual-rebates/delete',
                    name: 'Delete Rebate',
                    component: () =>
                        import ('./views/manual-rebates/ManualRebates.vue'),
                    beforeEnter: requireAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'Delete Rebate',
                        rule: 'isAllowedToViewRebates'
                    }
                },
            ],
        },
        // =============================================================================
        // PARTNER PAGE LAYOUTS
        // =============================================================================
        {
            path: '',
            component: () =>
                import ('@/layouts/main/PartnerView.vue'),
            children: [
                // =============================================================================
                // PAGES
                // =============================================================================
                {
                    path: '/partner/sign-on',
                    name: 'Partner Sign-On',
                    component: () =>
                        import ('./views/partner-external/PartnerSignOn.vue'),
                    // beforeEnter: requireClientAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: '',
                        rule: 'isGuest'
                    },
                    props: (route) => ({
                        source: route.params.source
                    }),
                },
                {
                    path: '/partner/current-cycle/:partnerName',
                    name: 'Current Cycle',
                    component: () => import ('./views/partner-external/CurrentCycle.vue'),
                    beforeEnter: requireClientAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: '',
                        rule: 'isGuest'
                    }
                },
                {
                    path: '/partner/past-payments/:partnerName',
                    name: 'Past Payments',
                    component: () =>
                        import ('./views/partner-external/PastPayments.vue'),
                    beforeEnter: requireClientAuth,
                    meta: {
                        buttons: {
                            refresh: true
                        },
                        pageTitle: 'Past Payments',
                        rule: 'isGuest'
                    }
                },
                // {
                //     path: '/partner/agreements/:partnerName',
                //     name: 'Client Agreements',
                //     component: () =>
                //         import ('./views/partner-external/ClientAgreements.vue'),
                //     beforeEnter: requireClientAuth,
                //     meta: {
                //         buttons: {
                //             refresh: true
                //         },
                //         pageTitle: 'Agreements',
                //         rule: 'isGuest'
                //     }
                // },
            ]
        },
        // =============================================================================
        // FULL PAGE LAYOUTS
        // =============================================================================
        {
            path: '',
            component: () =>
                import ('@/layouts/full-page/FullPage.vue'),
            children: [
                // =============================================================================
                // PAGES
                // =============================================================================
                {
                    path: '/login',
                    name: 'page-login',
                    component: () =>
                        import ('@/views/pages/Login.vue'),
                    meta: {
                        rule: 'isGuest'
                    }
                },
                {
                    path: '/error-404',
                    name: 'page-error-404',
                    component: () =>
                        import ('@/views/pages/Error404.vue'),
                    meta: {
                        rule: 'isGuest'
                    }
                }
            ]
        },
        // Redirect to 404 page, if no match found
        {
            path: '*',
            redirect: '/error-404'
        }
    ]
})

router.beforeEach((to, from, next) => {
    // Add body loading class
    const appBody = document.body;

    if (appBody) {
        appBody.classList.add("body-loading");
    }

    return next();
});

router.afterEach(() => {
    // Remove initial loading
    const appLoading = document.getElementById('loading-bg');

    if (appLoading) {
        appLoading.style.display = "none";
    }
    // Remove body loading class
    const appBody = document.body;
    if (appBody) {
        appBody.classList.remove("body-loading");
    }
})

export default router;